import styled from "styled-components";

/* ─────────────────────────────────────────
   ColorField
───────────────────────────────────────── */

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 8px 20px;
  transition: background 0.15s;

  &:hover {
    background: #f8f9fc;
  }

  @media (max-width: 600px) {
    gap: 12px;
    padding: 10px 14px;
  }
`;

export const Swatch = styled.label<{ $color: string | null; $isSet: boolean }>`
  position: relative;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  flex-shrink: 0;
  cursor: pointer;
  overflow: hidden;
  background: ${({ $color, $isSet }) => ($isSet && $color ? $color : "transparent")};
  border: ${({ $isSet }) => ($isSet ? "none" : "2px dashed #d1d5db")};
  box-shadow: ${({ $isSet, $color }) => ($isSet && $color ? `0 2px 8px ${$color}55` : "none")};
  transition: box-shadow 0.2s, border 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }

  input[type="color"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    padding: 0;
    border: none;
  }


`;

export const SwatchEmpty = styled.span`
  font-size: 15px;
  color: #d1d5db;
  user-select: none;
  line-height: 1;
`;

export const FieldMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  min-width: 0;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`;

export const FieldLabel = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  white-space: nowrap;
  flex-shrink: 0;
  width: 180px;

  @media (max-width: 600px) {
    font-size: 13px;
    width: auto;
  }
`;

export const FieldDescription = styled.span`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;

  @media (max-width: 600px) {
    font-size: 11px;
    flex: none;
    width: 100%;
  }
`;

export const FieldRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  justify-content: flex-end;
`;

export const HexBadge = styled.span`
  font-family: "Courier New", monospace;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 10px;
  letter-spacing: 0.5px;
`;

export const DefaultBadge = styled.span`
  font-size: 12px;
  color: #94a3b8;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 10px;
`;

export const ClearBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  padding: 2px;
  display: flex;
  align-items: center;
  font-size: 14px;
  transition: color 0.15s;
  line-height: 1;

  &:hover {
    color: #f87171;
  }
`;

/* ─────────────────────────────────────────
   Configuration (page layout)
───────────────────────────────────────── */

export const PageWrapper = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 22px 10px 10px 10px;
  gap: 28px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    gap: 16px;
    align-items: stretch;
  }
`;

export const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;

  @media (max-width: 900px) {
    height: auto;
    overflow: visible;
  }
`;

export const ScrollableGroups = styled.div`
  flex: 1;
  min-height: 0;
  max-height: 620px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    flex: none;
    max-height: none;
    overflow-y: visible;
  }
`;

export const PanelHeader = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 600px) {
    padding: 16px 18px;
    border-radius: 12px;
  }
`;

export const GroupCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;

  @media (max-width: 600px) {
    border-radius: 12px;
  }
`;

export const GroupHeader = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;

  @media (max-width: 600px) {
    padding: 11px 14px;
  }
`;

export const GroupAccentBar = styled.div<{ $color: string }>`
  width: 4px;
  height: 18px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const GroupTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  letter-spacing: 0.2px;
`;

export const GroupFieldCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: #94a3b8;
`;

export const FieldList = styled.div`
  display: flex;
  flex-direction: column;

  & > *:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

export const SaveRow = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 600px) {
    border-radius: 12px;
    padding: 14px 16px;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

export const RightPanel = styled.div`
  width: 360px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    flex-shrink: 1;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 200px;
  width: 100%;
`;

/* ─────────────────────────────────────────
   KioskPreview (phone frame)
───────────────────────────────────────── */

export const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: sticky;
  top: 24px;

  @media (max-width: 900px) {
    position: static;
    width: 100%;
  }
`;

export const PreviewLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  letter-spacing: 0.3px;
`;

export const ScreenToggle = styled.div`
  display: flex;
  background: #e2e8f0;
  border-radius: 10px;
  padding: 4px;
  gap: 3px;
`;

export const ToggleBtn = styled.button<{ $active: boolean }>`
  padding: 7px 18px;
  border-radius: 7px;
  border: none;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  background: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
  color: ${({ $active }) => ($active ? "#0f172a" : "#64748b")};
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? "0 1px 4px rgba(0,0,0,0.14)" : "none")};
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? "#ffffff" : "#cbd5e1")};
    color: ${({ $active }) => ($active ? "#0f172a" : "#334155")};
  }
`;

export const PhoneFrame = styled.div`
  width: 320px;
  height: 580px;
  border-radius: 32px;
  border: 8px solid #1a1a1a;
  background: #1a1a1a;
  overflow: hidden;
  box-shadow:
    0 0 0 1px #333,
    0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 480px) {
    width: 270px;
    height: 490px;
    border-radius: 26px;
    border-width: 6px;
  }
`;

export const PhoneNotch = styled.div`
  height: 20px;
  background: #1a1a1a;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  z-index: 1;
`;

export const Notch = styled.div`
  width: 70px;
  height: 10px;
  background: #111;
  border-radius: 0 0 8px 8px;
`;

export const PhoneScreen = styled.div`
  flex: 1;
  overflow: hidden;
  background: #fff;
  border-radius: 0 0 24px 24px;
`;

export const HintText = styled.div`
  font-size: 12px;
  color: #64748b;
  text-align: center;
`;

/* ─────────────────────────────────────────
   CategoriesScreen
───────────────────────────────────────── */

export const CatScreen = styled.div<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${({ $bg }) => $bg};
  overflow: hidden;
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: #ffffff;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
`;

export const SearchBar = styled.div`
  flex: 1;
  height: 28px;
  background: #f5f5f5;
  border-radius: 14px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 5px;
  font-size: 9px;
  color: #aaa;
`;

export const ClearCartBtn = styled.button<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 9px;
  font-weight: 600;
  cursor: default;
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`;

export const CatBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export const CatSidebar = styled.div<{ $bg: string }>`
  width: 72px;
  background: ${({ $bg }) => $bg};
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 4px;
  overflow-y: auto;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const CategoryCard = styled.div<{ $selected: boolean; $borderColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
  border: ${({ $selected, $borderColor }) =>
    $selected ? `2.5px solid ${$borderColor}` : "2px solid transparent"};
  padding: 5px 3px 4px;
  cursor: default;
  background: ${({ $selected }) => ($selected ? "#fff" : "transparent")};
`;

export const CategoryEmoji = styled.div`
  font-size: 22px;
  line-height: 1;
  margin-bottom: 3px;
`;

export const CategoryLabel = styled.span`
  font-size: 8px;
  font-weight: 500;
  color: #333;
  text-align: center;
  line-height: 1.2;
`;

export const CatMainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SectionTitle = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin-bottom: 8px;
`;

export const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

export const ItemCard = styled.div<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  border-radius: 8px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: relative;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

export const ItemImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  background: #f0ede8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 2px;
`;

export const BestSellerBadge = styled.div<{ $color: string }>`
  position: absolute;
  top: 4px;
  right: 4px;
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 6px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 3px;
  line-height: 1.2;
  text-align: center;
`;

export const CatItemName = styled.div<{ $color: string }>`
  font-size: 8px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  line-height: 1.2;
`;

export const CatItemDesc = styled.div<{ $color: string }>`
  font-size: 7px;
  color: ${({ $color }) => $color};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CatItemPrice = styled.div<{ $color: string }>`
  font-size: 9px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin-top: 2px;
`;

/* ─────────────────────────────────────────
   ItemDetailScreen
───────────────────────────────────────── */

export const DetailScreen = styled.div<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${({ $bg }) => $bg};
  overflow: hidden;
`;

export const DetailBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export const DetailSidebar = styled.div<{ $bg: string }>`
  width: 72px;
  background: ${({ $bg }) => $bg};
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 4px;
  flex-shrink: 0;
`;

export const SidebarCat = styled.div<{ $selected: boolean; $border: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
  border: ${({ $selected, $border }) =>
    $selected ? `2.5px solid ${$border}` : "2px solid transparent"};
  padding: 5px 3px 4px;
  background: ${({ $selected }) => ($selected ? "#fff" : "transparent")};
`;

export const DetailCatEmoji = styled.div`
  font-size: 22px;
  line-height: 1;
  margin-bottom: 3px;
`;

export const DetailCatLabel = styled.span`
  font-size: 8px;
  font-weight: 500;
  color: #333;
  text-align: center;
  line-height: 1.2;
`;

export const DetailMainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

export const BackBtn = styled.div<{ $color: string }>`
  background: #f5f5f5;
  border-radius: 6px;
  padding: 4px 7px;
  font-size: 9px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  cursor: default;
  flex-shrink: 0;
`;

export const ItemImageBox = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background: #f0ede8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  flex-shrink: 0;
`;

export const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const DetailItemName = styled.div<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  line-height: 1.2;
`;

export const ItemSubtitle = styled.div<{ $color: string }>`
  font-size: 8px;
  color: ${({ $color }) => $color};
`;

export const DetailItemPrice = styled.div<{ $color: string }>`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin-top: 2px;
`;

export const ModifierScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ModifierGroup = styled.div<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  border-radius: 8px;
  padding: 8px;
`;

export const ModifierHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

export const ModifierChip = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

export const ModifierRequired = styled.span<{ $color: string }>`
  font-size: 8px;
  color: ${({ $color }) => $color};
`;

export const OptionsRow = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const OptionCard = styled.div<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  background: ${({ $bg }) => $bg};
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  padding: 6px 8px;
  min-width: 54px;
  flex-shrink: 0;
`;

export const OptionEmoji = styled.div`
  font-size: 18px;
`;

export const OptionLabel = styled.div<{ $color: string }>`
  font-size: 8px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  text-align: center;
`;

export const ExtraPriceBadge = styled.div<{ $color: string }>`
  font-size: 7px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}18`};
  border: 1px solid ${({ $color }) => `${$color}40`};
  border-radius: 4px;
  padding: 1px 4px;
`;

export const BottomBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border-top: 1px solid #eee;
  flex-shrink: 0;
`;

export const QtyControl = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;

  button {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    border: none;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: default;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  span {
    font-size: 11px;
    font-weight: 600;
    color: #333;
    min-width: 12px;
    text-align: center;
  }
`;

export const AddToCartBtn = styled.div<{ $color: string }>`
  flex: 1;
  background: ${({ $color }) => $color};
  color: #fff;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: default;
`;

/* ─────────────────────────────────────────
   CartScreen
───────────────────────────────────────── */

export const CartPage = styled.div<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${({ $bg }) => $bg};
  overflow: hidden;
`;

export const OrderTypeRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 10px 6px;
  background: #fff;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

export const OrderTypeBtn = styled.div<{ $active: boolean; $color: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  border-radius: 8px;
  border: ${({ $active, $color }) => ($active ? `2px solid ${$color}` : "2px solid #e8e8e8")};
  background: #fff;
  cursor: default;

  span:first-child {
    font-size: 16px;
    line-height: 1;
  }

  span:last-child {
    font-size: 8px;
    font-weight: ${({ $active }) => ($active ? "700" : "500")};
    color: ${({ $active, $color }) => ($active ? $color : "#888")};
  }
`;

export const CartScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &::-webkit-scrollbar { display: none; }
`;

export const CartItemSection = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const CartItemTitle = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

export const CartQtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CartQtyBtn = styled.div<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  line-height: 1;
`;

export const CartQtyNum = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 10px;
  text-align: center;
`;

export const CartItemPrice = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

export const CartItemImg = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f0ede8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

export const CartModGroup = styled.div`
  border-left: 2px solid #e53935;
  padding-left: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const CartModGroupLabel = styled.div`
  font-size: 8px;
  font-weight: 700;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const CartModItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CartModItemName = styled.span<{ $color: string }>`
  font-size: 8px;
  color: ${({ $color }) => $color};
`;

export const CartModItemPrice = styled.span<{ $color: string }>`
  font-size: 8px;
  color: ${({ $color }) => $color};
`;

export const CartBackBtn = styled.div`
  border: 1.5px solid #ddd;
  border-radius: 10px;
  padding: 9px;
  text-align: center;
  font-size: 9px;
  font-weight: 600;
  color: #444;
  background: #fff;
  cursor: default;
`;

export const CartFooterSection = styled.div`
  background: #fff;
  border-top: 1px solid #eee;
  padding: 8px 10px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const CartUtensilsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 7px;
  color: #aaa;
`;

export const CartTotalsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CartTotalLabel = styled.span<{ $bold?: boolean; $color: string }>`
  font-size: ${({ $bold }) => ($bold ? "10px" : "8px")};
  font-weight: ${({ $bold }) => ($bold ? "700" : "400")};
  color: ${({ $color }) => $color};
`;

export const CartTotalValue = styled.span<{ $bold?: boolean; $color: string }>`
  font-size: ${({ $bold }) => ($bold ? "10px" : "8px")};
  font-weight: ${({ $bold }) => ($bold ? "700" : "400")};
  color: ${({ $color }) => $color};
`;

export const CartCheckoutBtn = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  color: #fff;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  cursor: default;
  margin-top: 2px;
`;
